<template lang="pug">
	.mraid-sdk-tutorial-settings(v-if="showed")
		.card(v-pseudo-modal-name="'mraid-sdk-tutorial-settings'")
			.card-header
				.header-title
					i.fa.fa-question-circle
					span MraidSDK Settings documentation
				.close-button(@click="hide()")
					i.fa.fa-close
			.card-content
				.tutorial-chapter-text
					p.description Global Settings object contain settings that can be viewed and changed in Dashboard. You can change default value of standard Dashboard settings or create your own custom setting that will be visible in the Dashboard UI.
					p.description To start working with settings, you should create a Settings folder inside your creative root folder and add one or more json files there with single json object inside each of them.
					p.description Minimal example of settings json file:
					code
						div {
						div &nbsp;&nbsp;"title": "Default",
						div &nbsp;&nbsp;"name": "default",
						div
						div &nbsp;&nbsp;"game-code-url": "dist/main.js",
						div &nbsp;&nbsp;"target-url": {"value": "https://example.com", "ios": "", "android": "", "amazon": "", "type": "string"},
						div
						div &nbsp;&nbsp;"track-events": {"value": [], "type": "info"},
						div
						div &nbsp;&nbsp;"Localization": {
						div &nbsp;&nbsp;},
						div
						div &nbsp;&nbsp;"Assets": {
						div &nbsp;&nbsp;}
						div }
					p.description You can copy this json into Settings/default.json file in your game creative. Then you should modify it according to your creative.
					p.description These settings will be added to your game automatically before your js code will be loaded. So you should check, if MraidSDK and Settings object already exist in the scope, do nothing. Else, read settings json file manually.
					code
						div import LocalSettings from "default.json";
						div if (!window.MraidSDK) window.Settings = LocalSettings;
					p.description There are a lot of other settings that will be added to Settings object by Dashboard. For example "cta-only", "tutorial", "locale" and many more. They will be described in this article.
					p.description If you do not want to support and show any standard setting (like 'cta-only' or 'tutorial') in the Dashboard UI, you can hide it using "isHidden" property:
					code
						div {
							div &nbsp;&nbsp;"tutorial": {..., "isHidden": true},
						div }
					p.description By default all of them will be displayed in the Dashboard UI.
				.tutorial-chapter-text
					h5
						.title(v-html="`<b>File name</b>, Settings['<b>title</b>'] and Settings['<b>name</b>']`")
						.snippet string
					p.description These settings json files will be displayed in the Dashboard as separated base versions of your game.
					p.description If you use several json files, you should change the name of each json file as well as title and name fields inside it to relevant values. For example: white-character.json (title "White Character", name "white-character") and red-character.json (title "Red Character", name "red-character")
					p.description If you use one settings file, you can keep file name, title and name fields as is ("default").
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>game-code-url</b>']`")
						.snippet string
					p.description All js files of your game should be combined into one minified js file using webpack or another solution for this. You should put the path to this file in this setting value. Also, Dashboard will not use any html files from your creative, so your js file should create canvas and other needed html elements dynamically.
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>target-url</b>']`")
						.snippet string
					p.description Here you should put the url to Apple App Store or Google Play or a link to a website to which the user will be redirected after the game.
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>track-events</b>']`")
						.snippet collection
					p.description In this setting you should provide information about your custom tracking events if you added it in a game by MraidSDK.track("Your Custom Track"); In this case Dashboard will display these events in tracking settings and provide possibility to disable/enable and rename it.
					p.description The format is:
					code
						div {
						div &nbsp;&nbsp;"track-events": {
						div &nbsp;&nbsp;&nbsp;&nbsp;"value": [
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"name": "Your Custom Track", "description": "Your event description"},
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"name": "Your Another Track", "description": "Your event description"},
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;...
						div &nbsp;&nbsp;&nbsp;&nbsp;]
						div &nbsp;&nbsp;}
						div }
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>custom-setting-1</b>']`")
						.snippet string / number / boolean / select
					p.description You can create your own custom settings as well. It will be displayed in the specified section of the settings in the Dashboard UI (main section recommended).
					p.description Example of custom settings:
					code
						div {
						div &nbsp;&nbsp;"setting-1": {"value": "my value", "type": "string", "section": "main", "control": "input", "title": "Character type", "description": ""},
						div &nbsp;&nbsp;"setting-2": {"value": "#004466", "type": "string", "section": "main", "control": "color", "title": "Character color", "description": ""},
						div &nbsp;&nbsp;"setting-3": {"value": 1, "type": "select", "section": "main", "control": "radio", "values": [1, 2], optionDescriptions: {"1": "Description 1", "2": "Description 2"}, "title": "Character gun", "description": ""},
						div &nbsp;&nbsp;"setting-4": {"value": 2, "type": "select", "section": "main", "control": "select", "values": [1, 2], optionDescriptions: {"1": "Description 1", "2": "Description 2"}, "title": "Character skill", "description": ""},
						div &nbsp;&nbsp;"setting-5": {"value": 0.2, "type": "number", "section": "main", "control": "range", "min": 0.1, "max": 5, "step": 0.1, "title": "Character speed", "description": ""},
						div }
					p.description If you want receive the changes of any setting in realtime (when your game launches inside Dashboard), you should add property "autoApplyChanges". In this case you will receive MraidSDK event "Setting Changed" when user changes this settings value in Dashboard UI:
					code
						div {
							div &nbsp;&nbsp;"setting-1": {..., "autoApplyChanges": true},
						div }
					p.description If you do not want to show your custom setting in the Dashboard UI, you can specify section: "none":
					code
						div {
							div &nbsp;&nbsp;"setting-1": {..., "section": "none"},
						div }
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>cta-only</b>']`")
						.snippet true / false
					p.description How to use:
					code if (Settings['cta-only']['value']) {if (!window.MraidSDK) showNativeEndScreen();} else showGameplay();
					p.description This setting gives possibility to skip game and show only End Card in the creative. MraidSDK can call "Show Native End Screen" event, if this setting turned on. You should subscribe on this event and show your native end screen there. Because of this "if (!window.MraidSDK)" exist in this code.
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>tutorial</b>']`")
						.snippet true / false
					p.description How to use:
					code if (Settings['tutorial']) doNothing(); else enableTutorial();
					p.description Disable or enable in-game tutorial based on this setting value. If you do not have a tutorial in game, you should disable this setting in your default.json settings file by adding "isHidden": true.
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>tutorial-timeout</b>']`")
						.snippet number
					p.description The time from "Game Starts" to tutorial showing, if user do nothing.
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>end-screen-click-out</b>']`")
						.snippet true / false
					p.description This setting controls the clickable area on the End Screen. You should look at it and make relevant changes in your native End Screen.
					p.description To implement this setting behavior in your native end screen use:
					code
						div if (Settings['end-screen-click-out']['value']) {
						div &nbsp;&nbsp;... make whole end screen clickable ...
						div } else {
						div &nbsp;&nbsp;... make only CTA button on the end screen clickable ...
						div };
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>end-screen-click-out-block</b>']`")
						.snippet number
					p.description This setting blocked click out possibility of the end screen for the specified time after the end screen is opened. In milliseconds. This setting works inside MraidSDK.open() method automatically. No needed any changes in your game code.
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings['<b>locale</b>']`")
						.snippet string
					p.description This setting control the current locale of the game. You can set manually "en", "es", "de" and other locales or set it to special value "auto". In this case locale will be detected automatically based on user device.
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings.<b>Localization</b>`")
						.snippet collection
					code
						div {
						div &nbsp;&nbsp;"Localization": {
						div &nbsp;&nbsp;&nbsp;&nbsp;"en": {
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"TEXT 1": {
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"type": "pixi",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"text": "Start play the game!",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"fontFamily": "TTNormsProBlack",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"fontSize": "90px"
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;},
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"TEXT 2": {
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"type": "pixi",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"text": "Game completed!",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"fontFamily": "TTNormsProBlack",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"fontSize": "90px"
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;}
						div &nbsp;&nbsp;&nbsp;&nbsp;},
						div &nbsp;&nbsp;&nbsp;&nbsp;"es": {
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"TEXT 1": {
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"type": "pixi",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"text": "¡Empieza a jugar el juego!",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"fontFamily": "TTNormsProBlack",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"fontSize": "90px"
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;},
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"TEXT 2": {
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"type": "pixi",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"text": "¡Juego completado!",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"fontFamily": "TTNormsProBlack",
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"fontSize": "90px"
						div &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;}
						div &nbsp;&nbsp;&nbsp;&nbsp;}
						div &nbsp;&nbsp;}
						div }
					p.description Here you should collect and store all text from your game as well as its styles. In this case Dashboard will provide you possibility to change these text and put different variation of these text for the different languages (localization).
					p.description You can use it like this:
					code
						div if (window.MraidSDK) yourGameTextObject.setText(MraidSDK.getMessage("TEXT 1")); else yourGameTextObject.setText(Settings.Localization.en["TEXT 1"].text);
						div if (window.MraidSDK) yourGameTextObject.setTextStyles(MraidSDK.getMessageStyles("TEXT 1")); else yourGameTextObject.setTextStyles(Settings.Localization.en["TEXT 1"]);
					p.description Method getMessage will return message according to current locale. You can set locale manually or use user device locale automatically. This behavior controlled by "locale" setting.
				.tutorial-chapter-text
					h5
						.title(v-html="`Settings.<b>Assets</b>`")
						.snippet collection
					code
						div {
						div &nbsp;&nbsp;"Assets": {
						div &nbsp;&nbsp;&nbsp;&nbsp;"logotype": {"type": "image", "url": "Images/logotype.png"},
						div &nbsp;&nbsp;&nbsp;&nbsp;"texture": {"type": "atlas", "url": "Textures/Texture.json"},
						div &nbsp;&nbsp;&nbsp;&nbsp;"click": {"type": "sound", "url": "Sounds/click.mp3"},
						div &nbsp;&nbsp;&nbsp;&nbsp;"video": {"type": "video", "url": "Videos/video.mp4"},
						div &nbsp;&nbsp;&nbsp;&nbsp;"TTNormsProBlack": {"type": "web-font", "url": "Fonts/TTNormsProBlack.css"},
						div &nbsp;&nbsp;&nbsp;&nbsp;"model1": {"type": "fbx", "url": "Data/model.fbx"},
						div &nbsp;&nbsp;&nbsp;&nbsp;"model2": {"type": "glb", "url": "Data/model.glb"},
						div &nbsp;&nbsp;&nbsp;&nbsp;"level": {"type": "json", "url": "Data/level.json"},
						div &nbsp;&nbsp;}
						div }
				p.description Here you should specify all game assets (images, atlases, sounds, fonts, 3D objects etc...). In this case Dashboard will show these files in Assets section of the settings and provide possibility to replace it.
</template>

<script>

	import Screen 				from './../../mixins/screen'

	export default {
		name: "MraidSDKTutorialSettings",
		mixins: [Screen],
		screen: {
			updateOn: [],
			events: {
				"Show MraidSDK Tutorial Settings": function() {

					this.show();

				}
			}
		},
		data() {

			return {
				showed: false
			};

		},
		methods: {
			collectData() {

			}
		}
	}
</script>

<style lang="sass">

	.app > .mraid-sdk-tutorial-settings
		position: absolute
		z-index: 2000
		left: 0
		top: 0
		right: 0
		bottom: 0
		background-color: rgba(0, 0, 0, 0.6)
		transition: opacity 0.3s ease-in-out

		.card-header
			display: flex
			flex-direction: row
			height: 60px

			.header-title
				width: auto
				margin-top: auto
				margin-bottom: auto
				font-size: 22px
				color: var(--warning)

				span
					padding-left: 10px

				i

			.close-button
				font-size: 22px
				color: var(--warning)
				opacity: 0.3
				cursor: pointer
				padding: 0 20px
				margin-left: auto
				margin-right: -20px

				&:hover
					opacity: 1

		.card
			width: 850px
			position: absolute
			left: 0
			right: 0
			top: 0
			bottom: 0
			margin: auto
			display: flex
			height: 460px
			box-shadow: 0 0 20px rgba(0, 0, 0, 0.8)
			border-top: 3px solid var(--warning)
			transition: transform 0.3s ease-in-out

			.card-content
				display: flex
				flex-direction: column
				height: 100%
				padding: 20px
				z-index: 2
				overflow: auto

				.caption

				.tutorial-chapter-text

					h5
						display: flex
						flex-direction: row
						align-items: center
						margin-top: 20px
						font-size: 1.1rem
						text-align: left
						font-weight: normal
						margin-bottom: 10px
						padding-bottom: 3px
						border-bottom: 1px solid var(--warning)
						user-select: text

					p
						margin-top: 0

					i
						margin-top: 3px
						margin-left: 10px
						margin-right: 5px

					.snippet
						font-size: 12px
						color: var(--primary)
						user-select: none
						margin-top: 3px
						margin-left: 5px

					.number
						font-size: 13px
						user-select: none
						color: var(--secondary)
						align-self: end
						padding-bottom: 1px
						margin-right: 5px

				.chapter-info
					text-indent: 0px
					margin-bottom: 10px

				b
					font-weight: normal
					color: var(--warning)

				code
					display: block
					margin: 10px

	.tutorial.showed
		pointer-events: all
		opacity: 1

	.tutorial.hiding
		opacity: 0

		.card


</style>

